import { useAuth } from "@clerk/react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "@/features/api/apiClient";
import { DeleteProjectDialog } from "@/features/projects/DeleteProjectDialog";
import { ProjectForm } from "@/features/projects/ProjectForm";
import { ProjectList } from "@/features/projects/ProjectList";
import {
  createProject,
  deleteProject,
  listProjects,
  updateProject,
} from "@/features/projects/projectApi";
import type { Project } from "@/features/projects/projectTypes";

export function Projects() {
  const { getToken, isLoaded } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUpgradeCta, setShowUpgradeCta] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

  async function loadProjects() {
    if (!isLoaded) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setShowUpgradeCta(false);

    try {
      const token = await getToken();
      const response = await listProjects(token);
      setProjects(response.projects);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load projects");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    void loadProjects();
  }, [isLoaded]);

  async function handleCreate(name: string) {
    setIsSaving(true);
    setError(null);
    setShowUpgradeCta(false);

    try {
      const token = await getToken();
      const response = await createProject(token, name);
      setProjects((currentProjects) => [
        response.project,
        ...currentProjects,
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to create project");
      setShowUpgradeCta(err instanceof ApiError && err.status === 403);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRename(name: string) {
    if (!editingProject) {
      return;
    }

    setIsSaving(true);
    setError(null);
    setShowUpgradeCta(false);

    try {
      const token = await getToken();
      const response = await updateProject(token, editingProject.id, { name });
      setProjects((currentProjects) =>
        currentProjects.map((project) =>
          project.id === response.project.id ? response.project : project,
        ),
      );
      setEditingProject(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to rename project");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    if (!projectToDelete) {
      return;
    }

    setIsDeleting(true);
    setError(null);
    setShowUpgradeCta(false);

    try {
      const token = await getToken();
      await deleteProject(token, projectToDelete.id);
      setProjects((currentProjects) =>
        currentProjects.filter((project) => project.id !== projectToDelete.id),
      );
      setProjectToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to delete project");
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="space-y-8">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-normal">
            My Projects
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            Create projects to organise your app ideas, generated blueprints,
            and build-ready prompts.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadProjects()}
          disabled={isLoading}
          className="w-fit rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Refresh
        </button>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h3 className="text-base font-semibold text-slate-950">
          Create project
        </h3>
        <div className="mt-4">
          <ProjectForm
            submitLabel="Create project"
            isSubmitting={isSaving}
            onSubmit={handleCreate}
          />
        </div>
      </section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <p>{error}</p>
          {showUpgradeCta ? (
            <Link
              to="/pricing"
              className="mt-3 inline-flex rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-800"
            >
              View pricing
            </Link>
          ) : null}
        </div>
      ) : null}

      {isLoading ? (
        <div className="grid gap-3">
          {[0, 1, 2].map((item) => (
            <div
              key={item}
              className="h-24 animate-pulse rounded-lg border border-slate-200 bg-white"
            />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <section className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
          <h3 className="text-lg font-semibold text-slate-950">
            No projects yet
          </h3>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">
            Start with a named project for an app idea. Your saved blueprints
            and Build Prompts will stay organised here.
          </p>
        </section>
      ) : (
        <ProjectList
          projects={projects}
          editingProjectId={editingProject?.id ?? null}
          onStartEdit={setEditingProject}
          onDelete={setProjectToDelete}
          renderEditForm={(project) => (
            <ProjectForm
              initialName={project.name}
              submitLabel="Save name"
              isSubmitting={isSaving}
              onSubmit={handleRename}
              onCancel={() => setEditingProject(null)}
            />
          )}
        />
      )}

      <DeleteProjectDialog
        project={projectToDelete}
        isDeleting={isDeleting}
        onCancel={() => setProjectToDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
